import { FC, ReactNode, memo, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { MUITooltip } from 'components/common/MUITooltip'
import { Nullable } from 'models/helpers'
import { ProductSegment } from 'constants/product/productTypes'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { SliderBar } from 'components/common/SliderBar'
import { SliderMark } from '@mui/material'
import Stack from '@mui/material/Stack'
import { TFunction } from 'i18next'
import { usePurchaseFlowConfig } from 'components/pages/PurchaseFlow/_main/contexts'
import { usePurchaseFlowProducts } from 'components/pages/PurchaseFlow/_main/contexts/PurchaseFlowProducts.context'

/** Props for the  SegmentLabelProps component. */
interface SegmentLabelProps {
  /** The translation function for i18n. */
  t: TFunction
  /** The key for the segment translation. */
  segmentKey: Nullable<string>
}

/**
 * SegmentLabel is a memoized component for rendering a segment label with i18n translation.
 * 
 * @example
 * <SegmentLabel t={t} segmentKey="yourSegmentKey" />
 */
const SegmentLabel = memo<SegmentLabelProps>(({ t, segmentKey }) => {
  return (
    <Trans t={t} i18nKey={segmentKey ? `segment:${segmentKey}` : 'step_product.none_selected'}>
      <sup></sup>
    </Trans>
  )
})

/**
 * Custom SliderMark props for using the Tooltip component together with the Mark component. 
 * Not the official props as there are none yet, only using the needed props.
 */
interface SliderMarkProps {
  /** The index of the mark. */
  'data-index': number
  /** The state of the owner mark component, containing marks with optional labels. */
  ownerState: {
    marks: { label?: string }[]
  }
}

/**
 * TooltipMark represents a custom mark component on a slider with an associated tooltip.
 * It displays a tooltip with the label of the current mark.
 *
 * @example
 * <TooltipMark data-index={index} ownerState={ownerState} />
 */
const TooltipMark = (props: SliderMarkProps) => {
  const index = props['data-index'] as number
  const currentMark = props.ownerState.marks[index]
  const tooltipLabel = currentMark?.label || ''

  return (
    <SliderMark {...props}>
      <MUITooltip content={tooltipLabel}>
        <span style={{ visibility: 'hidden' }}>aa</span>
      </MUITooltip>
    </SliderMark>
  )
}

/** 
 * Empty span component to avoid displaying the label bellow the marks.
 * Allows the TooltipMark component to display the label inside the Tooltip.
 */
const GhostLabel = () => (
  <span></span>
)

/** Props for the ProductSegmentSlider component. */
interface ProductSegmentSliderProps {
  /**
   * Overrides the default commit-on-change behavior. When provided, moving the slider calls this
   * instead of committing `setSegment` directly, letting the caller stage a pending selection (used
   * by the embed segment gate, where a "Weiter" button commits). When omitted, the slider commits
   * `setSegment` on change exactly as before (normal-flow behavior stays byte-identical).
   */
  onSelect?: (segment: ProductSegment, index: number) => void
  /**
   * Controlled segment shown in the value label. When provided, the label reflects this instead of
   * the committed `selectedSegment`, so a pending (not-yet-committed) band previews correctly.
   */
  pendingSegment?: Nullable<ProductSegment>
}

/**
 * Displays filter section with slider selector for choosing which area size (segment) user wants products for.
 *
 * @example
 * <ProductSegmentSlider onClick={() => alert("Well hello there!")} onChange={() => alert("General Kenobi!")} />
 */
export const ProductSegmentSlider: FC<ProductSegmentSliderProps> = ({ onSelect, pendingSegment }) => {

  const { t } = useTranslation(['order', 'segment', 'segment_title'])
  const { selectedCategory, availableSegments } = usePurchaseFlowConfig()
  const { setSegment, selectedSegment } = usePurchaseFlowProducts()

  // Mirrors the uncontrolled slider's thumb position (starts at min = -1, 'none selected')
  const [thumbIndex, setThumbIndex] = useState(-1)

  // In pending (controlled) mode the label previews the caller's staged band; otherwise it reflects
  // the committed selection exactly as before.
  const displaySegment = onSelect ? pendingSegment ?? null : selectedSegment

  const availableMarks = useMemo(() => {
    const allMarks: { value: number, label: string | ReactNode }[] = []
    availableSegments.forEach((segment, index) => {
      allMarks.push({
        value: index,
        label: <SegmentLabel t={t} segmentKey={segment} />
      })
    })
    return allMarks
  }, [availableSegments, t])

  return (
    <SectionedBorderBox title={t(`segment_title:${selectedCategory}`)}>
      <Stack>
        <SliderBar
          min={-1} // -1 represents undefined segment as 'none selected'
          step={1}
          sx={{
            marginTop: '2rem',
            // The value label is centered on the thumb, so at the extreme positions half of it
            // overflows the slider and gets clipped by the scroll container / iframe edge in
            // narrow (embed) layouts — anchor it inside the slider there instead (ISSUE-3228)
            ...(thumbIndex === -1 && {
              '& .MuiSlider-valueLabel': {
                left: 0,
                '&::before': { left: '6px' },
              },
            }),
            ...(thumbIndex === availableSegments.length - 1 && {
              '& .MuiSlider-valueLabel': {
                left: 'auto',
                right: 0,
                '&::before': { left: 'auto', right: '6px', transform: 'translate(50%, 50%) rotate(45deg)' },
              },
            }),
          }}
          max={availableSegments.length - 1}
          defaultValue={undefined}
          data-cy="product-segment-slider"
          valueLabelFormat={() =>
            <SegmentLabel
              t={t}
              segmentKey={displaySegment}
            />
          }
          marks={availableMarks}
          slots={{
            mark: TooltipMark,
            markLabel: GhostLabel
          }}
          onChange={(_, index) => {
            const segment = availableSegments[index as number]
            setThumbIndex(index as number)
            if (onSelect) onSelect(segment, index as number)
            else setSegment(segment, index as number)
          }}
        />
      </Stack>
    </SectionedBorderBox>
  )
}
